import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { axiosget, axiospost } from "../../helpers/httprequest";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import { Button } from "../../components/ui/Button";
import Loader from "../../components/Loader";
import swal from "../../helpers/swal";
import is_money from "../../helpers/is_money";
import { FiCheckCircle, FiSend, FiBookOpen, FiCreditCard, FiFileText } from "react-icons/fi";

const QuickApplySubmit = ({ quickApplySessionId, modeOfEntry }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  // Fetch session payment
  const { data: payments, isLoading } = useQuery({
    queryKey: ["generatedRRR"],
    queryFn: async () => {
      const req = await axiosget("ms_application_fee");
      return req?.data;
    },
  });

  const sessionPayment = payments && payments?.type === undefined
    ? payments.find((v) => v?.trxdata?.quickApplySessionId === quickApplySessionId)
    : null;

  const handleSubmit = async () => {
    if (!modeOfEntry) {
      swal("danger", "Mode of entry is not selected. Please go back to the Documents step.");
      return;
    }

    if (!sessionPayment) {
      swal("danger", "No payment found for this session.");
      return;
    }

    try {
      setIsSubmitting(true);
      const req = await axiospost("ms_applications", {
        programme: sessionPayment?.programme?._id,
        payment: sessionPayment?._id,
        type: modeOfEntry,
      });
      const { type, message } = req?.data;
      swal(type, message);
      if (type === "success") {
        setSubmitted(true);
      }
    } catch ({ message }) {
      swal("error", message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) return <Loader />;

  if (!sessionPayment) {
    return (
      <Alert variant="warning">
        <AlertDescription>
          No payment found for this session. Please go back to the Payment step.
        </AlertDescription>
      </Alert>
    );
  }

  if (submitted) {
    return (
      <div className="text-center py-12 space-y-6">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100">
          <FiCheckCircle className="h-10 w-10 text-green-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-neutral-900 mb-2">Application Submitted!</h2>
          <p className="text-neutral-600">
            Your application for <strong>{sessionPayment?.programme?.code} - {sessionPayment?.programme?.name}</strong> has been submitted successfully.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => navigate("/app/dashboard")}
        >
          Go to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
        <div className="px-6 py-4 bg-neutral-50 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">Application Summary</h3>
          <p className="text-sm text-neutral-500">Review your application details before submitting</p>
        </div>

        <div className="divide-y divide-neutral-100">
          {/* Programme */}
          <div className="px-6 py-4 flex items-center gap-4">
            <div className="p-2.5 rounded-lg bg-primary-50">
              <FiBookOpen className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <p className="text-xs text-neutral-500 uppercase tracking-wider font-medium">Programme</p>
              <p className="text-sm font-semibold text-neutral-900">
                {sessionPayment?.programme?.code} - {sessionPayment?.programme?.name}
              </p>
            </div>
          </div>

          {/* Payment Reference */}
          <div className="px-6 py-4 flex items-center gap-4">
            <div className="p-2.5 rounded-lg bg-green-50">
              <FiCreditCard className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-neutral-500 uppercase tracking-wider font-medium">Payment Reference</p>
              <p className="text-sm font-semibold text-neutral-900 font-mono">
                {sessionPayment?.trxcode}
              </p>
              <p className="text-xs text-neutral-500">{is_money(sessionPayment?.amount)}</p>
            </div>
          </div>

          {/* Mode of Entry */}
          <div className="px-6 py-4 flex items-center gap-4">
            <div className="p-2.5 rounded-lg bg-blue-50">
              <FiFileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-neutral-500 uppercase tracking-wider font-medium">Mode of Entry</p>
              <p className="text-sm font-semibold text-neutral-900">
                {modeOfEntry || <span className="text-orange-600">Not selected</span>}
              </p>
            </div>
          </div>
        </div>
      </div>

      {!modeOfEntry && (
        <Alert variant="warning">
          <AlertDescription>
            Please go back to the Documents step and select a mode of entry before submitting.
          </AlertDescription>
        </Alert>
      )}

      <div className="pt-2">
        <Button
          variant="primary"
          size="lg"
          fullWidth
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting || !modeOfEntry}
          rightIcon={!isSubmitting && <FiSend />}
        >
          {isSubmitting ? "Submitting Application..." : "Submit Application"}
        </Button>
      </div>
    </div>
  );
};

export default QuickApplySubmit;
